/**
 * Shared utility functions for PortalNavbar and PortalSidebar.
 * Extracted for testability and reuse.
 */

/**
 * Determine which top-level nav tab is active based on the current path.
 */
export function getActiveTab(pathname: string): string {
  if (pathname.startsWith('/portal/sent')) return 'sent';
  if (pathname.startsWith('/portal/orders')) return 'orders';
  if (pathname.startsWith('/portal/quality-copilot')) return 'quality-copilot';
  if (pathname.startsWith('/portal/tech-support-copilot')) return 'tech-support-copilot';
  if (pathname.startsWith('/portal/inbox-analytics')) return 'inbox-analytics';
  if (pathname.startsWith('/portal/analyst')) return 'analyst';
  if (pathname.startsWith('/portal/knowledge-banks')) return 'knowledge-banks';
  // The Briefing page (the reader's watches) is BOTH the executive "Briefing"
  // tab and a Quality Signals sub-tab; the navbar falls back to 'signals' when it has no
  // briefing tab to light (PortalNavbar resolveActiveTab).
  if (pathname.startsWith('/portal/signals/digest')) return 'briefing';
  if (pathname.startsWith('/portal/signals')) return 'signals';
  // Executive portal tabs (constants/execPortal.ts).
  if (pathname.startsWith('/portal/my-roadmap')) return 'my-roadmap';
  if (pathname.startsWith('/portal/meetings')) return 'meetings';
  if (pathname.startsWith('/portal/questions-for-me')) return 'questions-for-me';
  if (pathname.startsWith('/portal/roadmap')) return 'roadmap';
  if (pathname.startsWith('/portal/quality-issues')) return 'quality-issues';
  if (pathname.startsWith('/portal/customer-grading')) return 'customer-grading';
  if (pathname.startsWith('/portal/costs')) return 'costs';
  if (pathname.startsWith('/portal/portal-analytics')) return 'portal-analytics';
  if (pathname.startsWith('/portal/pricing-exemptions')) return 'pricing-exemptions';
  if (pathname.startsWith('/portal/missed-orders')) return 'missed-orders';
  if (pathname.startsWith('/portal/customers')) return 'customers';
  if (pathname.startsWith('/portal/my-salespeople')) return 'my-salespeople';
  if (pathname.startsWith('/portal/all-emails')) return 'all-emails';
  if (pathname.startsWith('/portal/settings')) return '';
  return 'inbox'; // default — inbox
}

/**
 * The tab to highlight given the tabs actually rendered: the raw
 * getActiveTab id when it is one of them, else the nearest parent
 * ('briefing' → 'signals' for readers whose bar has no My Briefing tab),
 * else '' (nothing lit).
 */
export function resolveActiveTab(activeTab: string, renderedTabIds: readonly string[]): string {
  if (renderedTabIds.includes(activeTab)) return activeTab;
  if (activeTab === 'briefing' && renderedTabIds.includes('signals')) return 'signals';
  return '';
}

/**
 * Extract user initials from a full name (e.g. "John Doe" → "JD").
 */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

/**
 * Return a CSS class name token for the action item dot color,
 * based on the notification type string.
 */
export function getActionDotVariant(type: string): 'red' | 'amber' | 'blue' {
  if (type.includes('urgent') || type.includes('complaint')) return 'red';
  if (type.includes('follow_up') || type.includes('delivery')) return 'amber';
  return 'blue';
}

// Re-export from the shared date utilities (single source of truth).
export { formatTimeAgo } from '../../utils/dateFormatters';
